import { defineMessages } from 'react-intl';
import { getLineItemStateName } from '@printdeal/business-logic';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import Order, { SanitizedLineItem, SanitizedOrder } from '../../../types/order/Order';
import { Environment } from '../../../helpers/env';

export const LIST_OF_ORDER_DAYS = [{
  value: 'Today',
  min: 0,
  max: 0,
},
{
  value: '1 day ago',
  min: 1,
  max: 1,
},
{
  value: '2 - 3 days ago',
  min: 2,
  max: 3,
},
{
  value: '4 - 7 days ago',
  min: 4,
  max: 7,
},
{
  value: 'Last week(7 to 14 days ago)',
  min: 8,
  max: 14,
},
{
  value: 'Last 30 days(14 to 30 days ago)',
  min: 15,
  max: 30,
},
{
  value: 'Last 60 days(30 to 60 days ago)',
  min: 31,
  max: 60,
},
{
  value: 'Last 90 days(60 to 90 days ago)',
  min: 61,
  max: 90,
},
{
  value: 'Last year(90 to 365 days ago)',
  min: 91,
  max: 365,
},
{
  value: 'Over a year(> 365 days ago)',
  min: 366,
  max: Infinity,
},
];

export const ORDERS_IN_SECTION = 2;

export const recentOrderEvents = {
  RECENT_ORDER_VIEW: 'homepage_recently_ordered_view',
  RECENT_ORDER_CLICK: 'homepage_recently_ordered_click',
};

export const handleAllOrdersClick = () => {
  addDataLayerEntry(
    recentOrderEvents.RECENT_ORDER_CLICK,
    {
      position: 'to_my_account',
      button: 'all_orders',
    },
  );
};

export const getOrderIdList = (orders: Order[]): Array<{
  orderNumber: string,
  lineItemId: string,
  lineItemStatus: string | null,
}> =>
  orders?.map((order) =>
    order.lineItems.map((lineItem) => ({
      orderNumber: order.id,
      lineItemId: lineItem.id,
      lineItemStatus: getLineItemStateName(lineItem, Environment),
    }))).flat().slice(0, 2);

export const getLineItemData = (
  orderData: SanitizedOrder | undefined,
  order: { orderNumber: string, lineItemId: string },
): SanitizedLineItem | undefined => (
  orderData?.lineItems?.find((lineItem) => lineItem.id === order.lineItemId));

export const getDiscountedPrice = (
  lineItem: SanitizedLineItem,
  order: SanitizedOrder,
): number => {
  let discountedPrice: number = 0;
  const lineItemTotalDetails = lineItem
    ? order?.totalDetails?.lineItemTotalDetail[lineItem.id] : null;

  let originalPrice: number = 0;
  if (typeof lineItem?.realTotalNet?.centAmount === 'number') {
    originalPrice = lineItem?.realTotalNet?.centAmount;
  } else if (typeof lineItem?.totalPrice.centAmount === 'number') {
    originalPrice = lineItem?.totalPrice.centAmount;
  }

  let discount: number = 0;
  if (typeof lineItemTotalDetails?.customerDiscountTotalNonVat?.centAmount === 'number') {
    discount = lineItemTotalDetails?.customerDiscountTotalNonVat?.centAmount;
  }
  discountedPrice = originalPrice + discount;
  return discountedPrice;
};

export const getRecentOrderStatus = (lineItem: SanitizedLineItem): string | null => {
  const stateName = getLineItemStateName(lineItem, Environment);
  return stateName;
};

export const recentOrderMessages = defineMessages({
  recentOrderSectionHeading: {
    id: 'home.recentOrder.sectionHeading',
    defaultMessage: 'Recently ordered',
  },
  recentOrderProductQuantity: {
    id: 'home.recentOrder.ProductQuantity',
    defaultMessage: 'pieces',
  },
  recentOrderDeliveryDate: {
    id: 'home.recentOrder.deliveryDate',
    defaultMessage: 'Expected delivery date is',
  },
  recentOrderReOrderLink: {
    id: 'home.recentOrder.reOrderLink',
    defaultMessage: 'Reorder',
  },
  recentOrderOrderDetailsButton: {
    id: 'home.recentOrder.orderDetailsButton',
    defaultMessage: 'Order details',
  },
  recentOrderAllOrdersButton: {
    id: 'home.recentOrder.allOrdersButton',
    defaultMessage: 'All orders',
  },
  recentOrderCompletedText: {
    id: 'home.recentOrder.orderCompleted',
    defaultMessage: 'Order completed on',
  },
});

const differenceInDates = (date1: Date, date2 = new Date()) => (date2.getTime() - date1.getTime());

const differenceInDatesInDays = (date1: Date, date2 = new Date()) => {
  // Get 1 day in milliseconds
  const oneDay = 1000 * 60 * 60 * 24;

  // Convert back to days and return
  return Math.round(differenceInDates(date1, date2) / oneDay);
};

export const getOrderAge = (date1: Date, date2 = new Date()) => {
  const diffInDays = differenceInDatesInDays(date1, date2);
  return LIST_OF_ORDER_DAYS.find((day) => (diffInDays >= day.min && diffInDays <= day.max))?.value;
};
